import React, { useState } from 'react';
import './VideoKinematicsNewAnalysis.css';
import { buildApiUrl } from '../config/api';

interface VideoKinematicsNewAnalysisProps {
  playerId: number;
  playerName?: string;
  onBack: () => void;
  onAnalysisComplete: (analysisData: any) => void;
}

type TabType = 'manual' | 'video';

interface DebugInfo {
  endpoint: string;
  statusCode: number | null;
  contentType: string | null;
  rawError: string | null;
}

const MOVEMENT_TYPES = [
  'Instep Shot – Power',
  'Driven Pass – Long Ball',
  'Short Push Pass – Side Foot',
  'Goalkeeper Distribution – Punt'
] as const;

const VideoKinematicsNewAnalysis: React.FC<VideoKinematicsNewAnalysisProps> = ({
  playerId,
  playerName,
  onBack,
  onAnalysisComplete
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('manual');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [movementType, setMovementType] = useState<string>('Instep Shot – Power');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);
  const [showDebug, setShowDebug] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Validate file type
    const validExtensions = ['.mp4', '.mov'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (!file.type.startsWith('video/') && !validExtensions.includes(fileExtension)) {
      setError('Please select a valid video file (.mp4 or .mov)');
      setSelectedFile(null);
      return;
    }

    // Validate file size (max 200MB)
    const maxSize = 200 * 1024 * 1024; // 200MB
    if (file.size > maxSize) {
      setError(`Video file is too large. Maximum size is 200MB. Your file is ${(file.size / (1024 * 1024)).toFixed(1)}MB.`);
      setSelectedFile(null);
      return;
    }

    // File is valid
    setSelectedFile(file);
    setError(null);
    setIsAnalyzing(false);
    setIsPending(false);
    setDebugInfo(null);
    setShowDebug(false);
  };

  const handleAnalyzeVideo = async () => {
    if (!selectedFile) {
      setError('Please select a video file first');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setDebugInfo(null);
    setShowDebug(false);
    setIsPending(false);

    // Use centralized API URL builder (declare outside try for catch block access)
    const endpoint = buildApiUrl('/video-kinematics/analyze-video');

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Log endpoint in dev mode
      if (process.env.NODE_ENV === 'development') {
        console.log(`[Video Upload] Posting to: ${endpoint}`);
        console.log(`[Video Upload] Token exists: ${!!token}`);
      }
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('player_id', playerId.toString());
      formData.append('movement_name', movementType);
      formData.append('age_band', 'U15–U17'); // Default, can be made configurable
      formData.append('level', 'Standard'); // Default, can be made configurable
      formData.append('kicking_foot', 'right'); // Default, can be made configurable

      // Add timeout to fetch request (30 seconds for video upload)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes for large video uploads
      
      let response: Response;
      try {
        response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
            // Note: Don't set Content-Type header for FormData - browser will set it automatically with boundary
          },
          body: formData,
          credentials: 'include',
          mode: 'cors',
          signal: controller.signal
        });
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          throw new Error('Request timed out. The video file may be too large or the server is taking too long to respond. Please try again with a smaller file.');
        }
        throw fetchError;
      }
      clearTimeout(timeoutId);

      // Log response status in dev mode
      if (process.env.NODE_ENV === 'development') {
        console.log(`Response status: ${response.status}`);
      }

      // Capture response metadata FIRST
      const statusCode = response.status;
      const contentType = response.headers.get('content-type') || '';
      const rawText = await response.text();
      
      // Only parse JSON if content-type indicates JSON
      let json: any = null;
      if (contentType.includes('application/json')) {
        try {
          json = JSON.parse(rawText);
        } catch (parseError) {
          // JSON parse failed, but we have rawText
          console.warn('Failed to parse JSON response:', parseError);
        }
      }

      // Handle pending/unavailable state (501, 503, or pending message)
      const isPendingStatus = statusCode === 501 || statusCode === 503;
      const hasPendingMessage = json?.status === 'pending' || 
                                (typeof json?.message === 'string' && json.message.toLowerCase().includes('pending')) ||
                                (typeof json?.detail === 'string' && json.detail.toLowerCase().includes('pending'));

      if (isPendingStatus || hasPendingMessage) {
        setIsPending(true);
        setError(null);
        setIsAnalyzing(false);
        return; // Don't throw, just show the pending UI
      }

      // Handle other error responses
      if (!response.ok) {
        const debugData: DebugInfo = {
          endpoint,
          statusCode,
          contentType,
          rawError: rawText.substring(0, 1000)
        };
        setDebugInfo(debugData);
        setShowDebug(true);

        // Extract error message from JSON if available
        let errorMessage = `Video analysis failed (HTTP ${statusCode})`;
        if (json) {
          const jsonError = json.error || json.message || json.detail;
          if (jsonError) {
            errorMessage += `. ${typeof jsonError === 'string' ? jsonError : JSON.stringify(jsonError)}`;
          }
        } else if (rawText) {
          errorMessage += `. ${rawText.substring(0, 200)}`;
        } else {
          errorMessage += '. Click for details.';
        }

        throw new Error(errorMessage);
      }

      // Response is OK, validate required fields
      if (!json) {
        const debugData: DebugInfo = {
          endpoint,
          statusCode,
          contentType,
          rawError: rawText.substring(0, 1000)
        };
        setDebugInfo(debugData);
        setShowDebug(true);
        throw new Error(`Invalid response format. Expected JSON but got ${contentType || 'unknown'}. Click for details.`);
      }

      // Validate response structure
      const data = json;
      const missingFields: string[] = [];
      
      if (!data.analysis && !data.analysisId) {
        missingFields.push('analysis or analysisId');
      }
      if (!data.analysis?.movement_name && !data.analysis?.movementType) {
        missingFields.push('movement_name or movementType');
      }
      if (!data.analysis?.metrics || Object.keys(data.analysis.metrics || {}).length === 0) {
        missingFields.push('metrics (or metrics is empty)');
      }
      // overallScore is not required - we'll compute it as fallback

      if (missingFields.length > 0) {
        const debugData: DebugInfo = {
          endpoint,
          statusCode,
          contentType,
          rawError: JSON.stringify(data, null, 2).substring(0, 1000)
        };
        setDebugInfo(debugData);
        setShowDebug(true);
        throw new Error(
          `Invalid response: Missing required fields: ${missingFields.join(', ')}. ` +
          `Response: ${JSON.stringify(data, null, 2).substring(0, 200)}... Click for details.`
        );
      }

      // Normalize knee flexion metrics (backward compatibility)
      let normalizedAnalysis = data.analysis;
      if (normalizedAnalysis?.metrics) {
        const { normalizeKneeFlexionMetrics } = await import('../utils/kinematicsNormalization');
        normalizedAnalysis = {
          ...normalizedAnalysis,
          metrics: normalizeKneeFlexionMetrics(normalizedAnalysis.metrics)
        };
      }
      
      // Extract or compute overallScore
      let overallScore: number | undefined = undefined;
      
      // Try multiple locations for overallScore (camelCase and snake_case)
      if (normalizedAnalysis?.overallScore !== undefined) {
        overallScore = normalizedAnalysis.overallScore;
      } else if (normalizedAnalysis?.overall_score !== undefined) {
        overallScore = normalizedAnalysis.overall_score;
      } else if (data.overallScore !== undefined) {
        overallScore = data.overallScore;
      } else if (data.overall_score !== undefined) {
        overallScore = data.overall_score;
      }
      
      // If overallScore is missing, compute it client-side as fallback
      if (overallScore === undefined && normalizedAnalysis?.metrics) {
        try {
          const { findBenchmark } = await import('../config/kinematicsBenchmarks');
          const { computeScoring } = await import('../utils/kinematicsScoring');
          
          const movementName = data.analysis.movementType || data.analysis.movement_name || movementType;
          const ageBand = data.analysis.ageBand || 'U15–U17';
          const level = data.analysis.level || 'Standard';
          
          const benchmark = findBenchmark(movementName, ageBand, level);
          if (benchmark) {
            const scoringResult = computeScoring(normalizedAnalysis.metrics, benchmark.params);
            overallScore = scoringResult.overallScore;
            console.log(`[VideoKinematics] Computed fallback overallScore: ${overallScore}`);
          } else {
            console.warn(`[VideoKinematics] No benchmark found for ${movementName}, ${ageBand}, ${level}, using 0`);
            overallScore = 0;
          }
        } catch (err) {
          console.error('[VideoKinematics] Failed to compute fallback overallScore:', err);
          overallScore = 0; // Fallback to 0 if computation fails
        }
      }
      
      // Ensure overallScore is set (default to 0 if still undefined)
      if (overallScore === undefined) {
        overallScore = 0;
      }
      
      // Add overallScore to normalized analysis object (both camelCase and snake_case for compatibility)
      const finalAnalysis = {
        ...normalizedAnalysis,
        overallScore,
        overall_score: overallScore,
        movementType: normalizedAnalysis.movementType || normalizedAnalysis.movement_name,
        metricsRangeStatus: normalizedAnalysis.metricsRangeStatus || data.analysis?.metricsRangeStatus || undefined // Include API-provided rangeStatus
      };

      // Response is valid, proceed with analysis
      if (data.success && finalAnalysis) {
        // Analysis complete - pass normalized data to results page
        onAnalysisComplete(finalAnalysis);
      } else if (finalAnalysis) {
        // Some responses might not have 'success' flag but have analysis
        onAnalysisComplete(finalAnalysis);
      } else {
        const debugData: DebugInfo = {
          endpoint,
          statusCode,
          contentType,
          rawError: JSON.stringify(data, null, 2).substring(0, 1000)
        };
        setDebugInfo(debugData);
        setShowDebug(true);
        throw new Error(
          `Analysis completed but no analysis data returned. ` +
          `Response: ${JSON.stringify(data, null, 2).substring(0, 200)}... Click for details.`
        );
      }
      
    } catch (err: any) {
      console.error('Video analysis error:', err);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to upload and analyze video';
      
      if (err.message) {
        errorMessage = err.message;
      } else if (err.name === 'TypeError' && err.message?.includes('fetch')) {
        errorMessage = 'Network error: Unable to connect to server. Please check your internet connection and try again.';
      } else if (err.name === 'AbortError') {
        errorMessage = 'Request was cancelled or timed out. Please try again.';
      } else if (err.message?.includes('CORS')) {
        errorMessage = 'CORS error: Unable to connect to server. Please contact support.';
      } else if (err.message?.includes('Load failed')) {
        errorMessage = 'Failed to load video file. Please ensure the file is a valid video format (MP4 or MOV) and try again.';
      }
      
      setError(errorMessage);
      
      // Set debug info for troubleshooting
      if (!debugInfo) {
        setDebugInfo({
          endpoint,
          statusCode: 0,
          contentType: 'unknown',
          rawError: err.toString() + '\n' + (err.stack || '')
        });
        setShowDebug(true);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleManualEntry = () => {
    // Navigate to manual entry form or show form inline
    // For now, we'll use the existing VideoKinematicsPage with example data
    // In production, this could open a form to manually enter metrics
    const exampleData = {
      movement_name: "Instep Shot – Power",
      ageBand: "U15–U17",
      level: "Standard",
      kickingFoot: "right",
      timestamp: new Date().toISOString(),
      video: {
        fps: 30.1,
        frameCount: 175,
        width: 1350,
        height: 1080,
        frameStep: 1
      },
      keyFrames: {
        backswing_frame: 13,
        contact_frame: 111,
        follow_through_frame: 114
      },
      metrics: {
        backswing_knee_flexion_deg: 93.3,
        plant_knee_flexion_deg: 141.7,
        trunk_forward_lean_deg: 0.6,
        hip_rotation_deg: 90.0,
        approach_angle_deg: 25.8,
        foot_speed_mps: 29.8,
        contact_time_ms: 33.2,
        follow_through_angle_deg: 110.0
      }
    };
    
    onAnalysisComplete(exampleData);
  };

  return (
    <div className="video-kinematics-new-analysis">
      <div className="new-analysis-header">
        <button className="back-button" onClick={onBack}>
          ← Back
        </button>
        <h1>New Video Kinematics Analysis</h1>
        {playerName && (
          <p className="player-name">{playerName}</p>
        )}
      </div>

      <div className="new-analysis-content">
        <div className="tab-switcher">
          <button
            className={`tab-button ${activeTab === 'manual' ? 'active' : ''}`}
            onClick={() => setActiveTab('manual')}
          >
            Manual Entry
          </button>
          <button
            className={`tab-button ${activeTab === 'video' ? 'active' : ''}`}
            onClick={() => setActiveTab('video')}
          >
            Video Upload
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'manual' && (
            <div className="manual-entry-tab">
              <div className="tab-description">
                <p>Manually enter kinematic metrics for analysis.</p>
                <p className="note">Note: Manual entry is useful for testing or when video analysis is not available.</p>
              </div>
              <button
                className="action-button primary"
                onClick={handleManualEntry}
              >
                Continue with Example Data
              </button>
              <p className="small-note">
                In production, this would open a form to enter all metrics manually.
              </p>
            </div>
          )}

          {activeTab === 'video' && (
            <div className="video-upload-tab">
              <div className="tab-description">
                <p>Upload a side-view soccer movement video for automatic analysis.</p>
                <p className="note">Supported formats: MP4, MOV. Maximum file size: 200MB.</p>
              </div>

              {/* Movement Type Selection */}
              <div className="form-group" style={{ marginBottom: '1.5rem', textAlign: 'left', maxWidth: '400px', margin: '0 auto 1.5rem' }}>
                <label htmlFor="movement-type" style={{ display: 'block', marginBottom: '0.5rem', color: '#e0e0e0', fontWeight: 500 }}>
                  Movement Type <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <select
                  id="movement-type"
                  value={movementType}
                  onChange={(e) => setMovementType(e.target.value)}
                  disabled={isAnalyzing}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #383b48',
                    borderRadius: '5px',
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                    background: '#11121b',
                    color: '#fdfeff',
                    cursor: isAnalyzing ? 'not-allowed' : 'pointer'
                  }}
                >
                  {MOVEMENT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              {/* Pending/Unavailable Notice */}
              {isPending && (
                <div className="pending-notice">
                  <div className="pending-header">
                    <strong>Video analysis not enabled yet</strong>
                  </div>
                  <div className="pending-body">
                    <p>The MediaPipe analysis service is not active. Please use Manual Entry for now.</p>
                    <button
                      className="action-button primary"
                      onClick={() => setActiveTab('manual')}
                      style={{ marginTop: '1rem' }}
                    >
                      Go to Manual Entry
                    </button>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && !isPending && (
                <div className="error-message">
                  <div className="error-content">
                    <strong>{error}</strong>
                    {debugInfo && (
                      <button
                        className="debug-toggle"
                        onClick={() => setShowDebug(!showDebug)}
                        type="button"
                      >
                        {showDebug ? '▼ Hide' : '▶ Show'} Details
                      </button>
                    )}
                  </div>
                  {showDebug && debugInfo && (
                    <div className="debug-panel">
                      <div className="debug-section">
                        <strong>Endpoint:</strong>
                        <code>{debugInfo.endpoint}</code>
                      </div>
                      <div className="debug-section">
                        <strong>Status Code:</strong>
                        <code>{debugInfo.statusCode ?? 'N/A'}</code>
                      </div>
                      <div className="debug-section">
                        <strong>Content-Type:</strong>
                        <code>{debugInfo.contentType ?? 'N/A'}</code>
                      </div>
                      {debugInfo.rawError && (
                        <div className="debug-section">
                          <strong>Raw Response (first 1000 chars):</strong>
                          <pre className="debug-pre">{debugInfo.rawError}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* File Selection */}
              <div className="upload-area">
                <input
                  type="file"
                  id="video-upload"
                  accept="video/mp4,video/mov,video/quicktime"
                  onChange={handleFileSelect}
                  disabled={isAnalyzing}
                  style={{ display: 'none' }}
                />
                <label htmlFor="video-upload" className="upload-label">
                  {selectedFile ? (
                    <div className="file-selected">
                      <div className="upload-icon">✓</div>
                      <p className="upload-text">{selectedFile.name}</p>
                      <p className="upload-hint">{(selectedFile.size / (1024 * 1024)).toFixed(1)} MB</p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setSelectedFile(null);
                          setError(null);
                        }}
                        className="change-file-button"
                        disabled={isAnalyzing}
                      >
                        Change File
                      </button>
                    </div>
                  ) : (
                    <div className="upload-prompt">
                      <div className="upload-icon">📹</div>
                      <p className="upload-text">Click to select video file</p>
                      <p className="upload-hint">or drag and drop here</p>
                    </div>
                  )}
                </label>
              </div>

              {/* Analyze Button */}
              <div style={{ marginTop: '2rem' }}>
                <button
                  className="action-button primary"
                  onClick={handleAnalyzeVideo}
                  disabled={!selectedFile || isAnalyzing}
                  style={{
                    opacity: (!selectedFile || isAnalyzing) ? 0.6 : 1,
                    cursor: (!selectedFile || isAnalyzing) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isAnalyzing ? (
                    <>
                      <span className="spinner-small"></span>
                      <span style={{ marginLeft: '0.5rem' }}>Analyzing video...</span>
                    </>
                  ) : (
                    'Analyze Video'
                  )}
                </button>
              </div>

              {/* Analysis Progress */}
              {isAnalyzing && (
                <div className="upload-progress">
                  <p>Processing video with MediaPipe pipeline...</p>
                  <p className="progress-note">This may take a few moments.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoKinematicsNewAnalysis;

